import {
  Controller,
  useFieldArray,
  Control,
  FieldErrors,
} from 'react-hook-form';

import { styled } from '@mui/material';

import { useI18n } from '@app-velocity/i18n';
import {
  Form,
  Select,
  TextField,
  Button,
  ButtonsContainer,
} from '@app-velocity/ui';
import { AddIcon, RemoveIcon } from '@app-velocity/icons';

import { TeamMemberDto } from '../../dto';

export type HasMembers<TMember = TeamMemberDto> = {
  members?: TMember[];
};

type TeamMembersFieldsProps<TForm extends HasMembers> = {
  control: Control<TForm>;
  errors?: FieldErrors<TForm>;
};

const StyledFieldContainer = styled(Form.FieldContainer)<{
  withDelete: boolean;
}>(({ theme, withDelete }) => ({
  gap: theme.spacing(2),
  display: 'grid',
  gridTemplateColumns: withDelete ? '7fr 2fr 1fr' : '7fr 2fr',
  alignItems: 'center',
}));

const StyledContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  alignItems: 'flex-start',
}));

const StyledInnerContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  alignItems: 'flex-start',
}));

const StyledDeleteButton = styled('button')({
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  svg: {
    fill: 'var(--error-error-color)',
  },
});

const StyledMainLabel = styled(Form.Label)({
  margin: 0,
});

export const TeamMembersFields = <TForm extends HasMembers>({
  control,
  errors,
}: TeamMembersFieldsProps<TForm>) => {
  const { t } = useI18n();

  const { fields, append, remove } = useFieldArray<HasMembers>({
    control: control as unknown as Control<HasMembers>,
    name: 'members',
  });

  const canDelete = fields.length > 0;

  const addMember = () => {
    append({ email: '', role: 'MEMBER' });
  };

  return (
    <StyledContainer>
      <StyledMainLabel>{t('teams:members')}</StyledMainLabel>
      <StyledInnerContainer>
        {fields.map((_, index) => {
          const itemErrors = (errors as FieldErrors<HasMembers>)?.members?.[
            index
          ];

          return (
            <StyledFieldContainer
              key={index}
              withDelete={canDelete}
              errorMessage={itemErrors?.email?.message}
            >
              <TextField
                size="medium"
                name={`members.${index}.email`}
                control={control as unknown as Control<HasMembers>}
                fullWidth
                error={!!itemErrors?.email}
                placeholder={t('teams:new_team.member_email_placeholder')}
              />

              <Controller
                name={`members.${index}.role`}
                control={control as unknown as Control<HasMembers>}
                render={({ field, fieldState }) => (
                  <Select
                    MenuProps={{ disablePortal: true }}
                    size="medium"
                    {...field}
                    error={!!fieldState.error}
                    variant="filled"
                  >
                    <Select.Item value="MEMBER">
                      {t('teams:new_team.member')}
                    </Select.Item>
                    <Select.Item value="ADMIN">
                      {t('teams:new_team.admin')}
                    </Select.Item>
                  </Select>
                )}
              />

              {canDelete && (
                <StyledDeleteButton type="button" onClick={() => remove(index)}>
                  <RemoveIcon />
                </StyledDeleteButton>
              )}
            </StyledFieldContainer>
          );
        })}
      </StyledInnerContainer>
      <ButtonsContainer align="start">
        <Button
          variant="text"
          color="inherit"
          type="button"
          onClick={addMember}
        >
          <AddIcon />
          {t('teams:new_team.add_member')}
        </Button>
      </ButtonsContainer>
    </StyledContainer>
  );
};
